<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-container">
      <div class="modal-header">
        <button
          type="button"
          class="modal-close-button"
          @click="close"
          aria-label="Close modal"
        >
          &times;
        </button>
        <h3>{{ title }}</h3>
      </div>
      <div class="modal-body">
        <div class="validation-message">
          <div class="warning-icon">{{ icon }}</div>
          <h4>{{ message }}</h4>
          <p v-if="description" class="description">
            {{ description }}
          </p>
        </div>

        <ul v-if="errors.length > 0" class="missing-fields-list">
          <li v-for="(error, index) in errors" :key="index" class="field-item">
            <span class="bullet">•</span>
            <span class="field-name">{{ error }}</span>
          </li>
        </ul>
      </div>
      <div class="modal-footer">
        <button class="ok-button" @click="close">
          {{ buttonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';

export default defineComponent({
  name: 'ValidationModal',
  props: {
    title: {
      type: String,
      default: 'Section Incomplete',
    },
    message: {
      type: String,
      default: 'Please complete the following fields:',
    },
    description: {
      type: String,
      default: '',
    },
    errors: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    icon: {
      type: String,
      default: '⚠️',
    },
    buttonText: {
      type: String,
      default: 'Got it',
    },
  },
  emits: ['close'],
  setup(_, { emit }) {
    const close = () => {
      emit('close');
    };

    return {
      close,
    };
  },
});
</script>

<style scoped>
.modal-overlay {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.validation-message {
  text-align: center;
  margin-bottom: 1.5rem;
}

.warning-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.validation-message h4 {
  margin: 0 0 0.5rem 0;
  color: var(--color-heading);
  font-size: 1.1rem;
}

.description {
  color: var(--color-text);
  font-size: 0.9rem;
  opacity: 0.8;
  margin: 0;
}

.missing-fields-list {
  list-style: none;
  padding: 0;
  margin: 0;
  background-color: var(--color-background-soft);
  border-radius: 0.75rem;
  overflow: hidden;
}

.field-item {
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-bottom: 0.0625rem solid var(--color-border);
}

.field-item:last-child {
  border-bottom: none;
}

.bullet {
  color: var(--vt-c-accent);
  font-weight: bold;
}

.field-name {
  color: var(--color-text);
  font-size: 0.95rem;
  font-weight: 500;
}

.ok-button {
  width: 100%;
  background: #0f172a !important;
  color: var(--vt-c-accent-dark) !important;
  border: 1px solid var(--vt-c-accent-dark) !important;
  border-radius: 999px;
  font-weight: 700;
}

.ok-button:hover,
.ok-button:focus-visible {
  background: #16233f !important;
  color: var(--vt-c-accent) !important;
  border-color: var(--vt-c-accent) !important;
  transform: none;
}
</style>
